/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Loader2, LogOut, ScrollText } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { LEGAL_OWNER } from '@/lib/legalOwner';
import {
  EULA_CHECKBOX_LABEL,
  EULA_TEXT,
  EULA_TITLE,
  EULA_VERSION,
  needsEulaGate,
} from '@/config/ipProtection';

/**
 * EulaGate — mandatory Legal Disclosure modal shown the very first time a
 * non-admin user logs into the demo. Cannot be dismissed (no overlay click,
 * no Escape, no close button): the user must tick the checkbox and confirm,
 * or leave the session.
 *
 * Acceptance is persisted server-side ONLY (`POST /api/legal/eula/accept` →
 * user.eula_accepted_at + audit `eula.accept`); nothing is written to
 * localStorage. A memory flag keeps the modal closed while /auth/me refreshes.
 */
export const EulaGate = () => {
  const { user, loading, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [acceptedInSession, setAcceptedInSession] = useState(null); // user.id che ha appena accettato (solo memoria)
  const { run, busy } = useAsyncAction();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!user) { setChecked(false); setAcceptedInSession(null); }
  }, [user]);

  const open = !loading && Boolean(user) && needsEulaGate(user) && acceptedInSession !== user?.id;

  const accept = () => run(async () => {
    await api.post('/legal/eula/accept', { version: EULA_VERSION, accepted: true });
    setAcceptedInSession(user?.id || null);
    await refreshUser();
  }, { success: 'Termini di Riservatezza accettati. Benvenuto nella demo.' });

  const decline = async () => {
    setLeaving(true);
    try { await logout(); } finally {
      setLeaving(false);
      navigate('/welcome', { replace: true });
    }
  };

  if (!open) return null;

  return (
    <AlertDialog open>
      <AlertDialogContent
        className="max-w-xl rounded-2xl"
        data-testid="eula-gate-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <ShieldAlert className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <AlertDialogTitle className="font-display text-lg leading-tight" data-testid="eula-gate-title">{EULA_TITLE}</AlertDialogTitle>
              <p className="text-[11px] text-muted-foreground mt-0.5">Versione {EULA_VERSION} · Legge 22 aprile 1941, n. 633</p>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="rounded-xl border border-border bg-muted/40 p-4 max-h-[40vh] overflow-y-auto">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            <ScrollText className="h-3.5 w-3.5" aria-hidden="true" /> Informativa di riservatezza
          </div>
          <AlertDialogDescription className="text-sm leading-relaxed text-foreground" data-testid="eula-gate-text">
            {EULA_TEXT}
          </AlertDialogDescription>
          <p className="text-[11px] text-muted-foreground mt-3">
            Titolare dei diritti: {LEGAL_OWNER.fullName} · CF {LEGAL_OWNER.fiscalCode} · {LEGAL_OWNER.jurisdiction}
          </p>
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
          <Checkbox
            id="eula-accept"
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            disabled={busy || leaving}
            className="mt-0.5"
            data-testid="eula-gate-checkbox"
          />
          <Label htmlFor="eula-accept" className="text-sm font-medium leading-snug cursor-pointer select-text">
            {EULA_CHECKBOX_LABEL}
            <span className="block text-xs text-muted-foreground font-normal mt-0.5">
              L'accettazione viene registrata con data/ora e utente nell'audit log immutabile (SHA-256).
            </span>
          </Label>
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl h-11"
            onClick={decline}
            disabled={busy || leaving}
            data-testid="eula-gate-decline-btn"
          >
            {leaving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <LogOut className="h-4 w-4" aria-hidden="true" />}
            Non accetto — esci
          </Button>
          <Button
            type="button"
            className="rounded-xl h-11"
            onClick={accept}
            disabled={!checked || busy || leaving}
            data-testid="eula-gate-accept-btn"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Accetto e continuo
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default EulaGate;
